import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function CopyStructureButton({ projectId, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" className="w-full" onClick={() => setOpen(true)} data-testid="copy-structure-btn"><Copy className="w-4 h-4 mr-1.5" />Copy Project Structure</Button>
      {open && <CopyStructureDialog projectId={projectId} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone(); }} />}
    </>
  );
}

function CopyStructureDialog({ projectId, onClose, onDone }) {
  const [projects, setProjects] = useState([]);
  const [source, setSource] = useState("");
  const [pv, setPv] = useState(null);
  const [mode, setMode] = useState("add_missing");
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/projects").then((r) => setProjects((r.data.items || []).filter((p) => p.id !== projectId))).catch(() => {}); }, [projectId]);
  useEffect(() => {
    setPv(null);
    if (source) api.get(`/projects/${projectId}/copy-structure/preview`, { params: { source_id: source } }).then((r) => setPv(r.data)).catch((e) => toast.error(apiError(e)));
  }, [source, projectId]);
  const nonEmpty = pv && (pv.target_milestones || pv.target_tasks);
  const run = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/copy-structure`, { source_id: source, mode: nonEmpty ? mode : "add_missing", confirm_replace: confirm });
      toast.success(`Copied ${data.milestones_added} milestone(s) and ${data.tasks_added} task(s)`); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="copy-structure-dialog">
        <DialogHeader><DialogTitle>Copy Project Structure</DialogTitle>
          <DialogDescription>Copies milestones and tasks (titles, details, order, visibility, default assignment, checklist). No status, comments, history or named assignees are copied.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold">Source Project</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="copy-source-select"><SelectValue placeholder="Choose a project" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.human_id} — {p.name}</SelectItem>)}</SelectContent>
            </Select></div>
          {pv && <p className="text-sm text-slate-600" data-testid="copy-preview">Source has {pv.source_milestones} milestone(s) and {pv.source_tasks} task(s).</p>}
          {nonEmpty ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="copy-existing">
              <p className="text-sm text-slate-700">This Project already contains {pv.target_milestones} Milestone(s) and {pv.target_tasks} Task(s). How would you like to proceed?</p>
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === "add_missing"} onChange={() => setMode("add_missing")} data-testid="copy-mode-add" />Add missing structure only ({pv.missing_milestones} milestone(s), {pv.missing_tasks} task(s))</label>
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="copy-mode-replace" />Replace existing structure</label>
              {mode === "replace" && <label className="flex gap-2 text-xs text-rose-700 bg-rose-50 rounded p-2"><input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="copy-confirm-replace" />
                <span><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />I understand the existing {pv.target_milestones} milestone(s) and {pv.target_tasks} task(s) in this Project will be removed.</span></label>}
            </div>
          ) : null}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-strong" disabled={!pv || saving || (nonEmpty && mode === "replace" && !confirm)} onClick={run} data-testid="copy-structure-apply">{saving ? "Copying…" : "Copy Structure"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
